import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, usePropertyField, EditableToken } from '@vibesites/runtime/react';
import { useScrollReveal } from '@vibesites/theme-preferred/hooks/useScrollReveal';
import { useRevealStyles } from '@vibesites/theme-preferred/hooks/useRevealStyles';

// ============================================================================
// Component
// ============================================================================

/**
 * Standalone CTA that opens the CORT lead form modal via window.Cort_modal.open().
 * Renders nothing unless the property has a `custom_cort_lead_form_id` set — that
 * same field is what loads the CORT script, so the button is meaningless without it.
 * Button text is an editable content token (override per page/property).
 */
export function CortLeadFormCta() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const cortLeadFormId = usePropertyField('custom_cort_lead_form_id') as string || '';
  // Default is derived from the contentSchema below; no need to pass it here.
  const buttonText = useContentToken('cortleadformcta.button_text');

  const openCortModal = React.useCallback(() => {
    const cort = typeof window !== 'undefined'
      ? (window as unknown as { Cort_modal?: { open?: () => void } }).Cort_modal
      : undefined;
    cort?.open?.();
  }, []);

  if (!cortLeadFormId) return null;

  return (
    <section ref={sectionRef} className="reveal-fade-up relative z-10 pt-10 pb-0 md:pt-14 md:pb-0 bg-[var(--vibe-color-background)]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <button
          type="button"
          onClick={openCortModal}
          className="group/cort relative inline-flex max-w-full items-center gap-1.5 whitespace-nowrap px-4 py-3 text-[0.7rem] font-semibold tracking-[0.08em] uppercase rounded-full text-[var(--vibe-color-text-on-primary,white)] transition-all duration-300 hover:-translate-y-0.5 sm:gap-2.5 sm:px-8 sm:py-3.5 sm:text-sm sm:tracking-[0.15em]"
          style={{
            background: 'linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%)',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2), 0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent)',
          }}
        >
          <EditableToken path="cortleadformcta.button_text" as="span" className="relative z-10 whitespace-nowrap">
            {buttonText || 'Furnish Your Home with CORT'}
          </EditableToken>
          <svg className="relative z-10 h-3.5 w-3.5 shrink-0 transition-transform duration-300 group-hover/cort:translate-x-0.5 sm:h-4 sm:w-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
          </svg>
        </button>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CortLeadFormCta',
  displayName: 'CORT Lead Form CTA',
  tokens: {
    'cortleadformcta.button_text': {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'Furnish Your Home with CORT',
    },
  },
};

(CortLeadFormCta as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CortLeadFormCta;
